import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { SIZE_LABELS, sizeFromHours, sizeRangeLabel } from '@rundown/domain';
import type { Priority, WorkType } from '@rundown/types';
import { formatHours } from '../../lib/format';
import { cx } from '../../lib/cx';
// The dense field primitives, shared with Settings. They were duplicated
// verbatim in both files; see components/Field.tsx.
import { CommittedField, CONTROL_CLASS, FieldShell, ReadOnlyField, SelectField } from '../../components';
import { useCapabilities } from '../workspace/useCapability';
import { useSizeThresholds, useVendorName } from '../workspace/useWorkspace';
import { sprintsQueryOptions } from '../sprints/api';
import { projectionQueryOptions } from '../backlog/api';
import { deliveryProjectsQueryOptions } from '../projects/api';
import { isInternalRequest, type RequestPatch, type RequestRow } from './api';
import { useUpdateRequest } from './useRequestMutations';
import { PRIORITY_LABELS, TYPE_LABELS } from './labels';

/**
 * The Fields panel — read-only detail for a client, an editing surface for
 * Noorix.
 *
 * Three decisions in here worth stating, because each of them is a place the
 * obvious implementation is wrong:
 *
 *  - **One field per request.** Each control sends only what it changed. The
 *    server writes an activity row per changed field, so a form that PUT the
 *    whole object would fill the trail with entries for fields nobody touched,
 *    and would overwrite a colleague's concurrent edit with a stale value read
 *    when the screen loaded.
 *
 *  - **No local mirror of the row.** The inputs read from the server's copy, and
 *    only the text ones keep a draft while they are being typed into. A
 *    component that copies the request into state at mount goes stale the moment
 *    a realtime event updates the cache, and then writes that staleness back.
 *
 *  - **Editability is per field, not per panel.** The classification fields are
 *    `request.review`, the sprint is `planning.write` and the project is
 *    `project.manage` — three gates, which a Developer happens to hold all of
 *    and a Client Owner holds exactly one of. The server checks them separately
 *    and so does this.
 *
 *  - **Status is not here.** It used to be, as a "Board column" select, and it
 *    was the third thing on this screen saying where the work had got to: the
 *    chip at the top, the progress track below the panel, and a select using a
 *    fourth vocabulary ("Staging") for the same six values the other two called
 *    something else. The control now lives in that progress section, where the
 *    word it changes is written next to it — see RequestDetailPage's
 *    `DeliveryColumn`.
 */

const TYPE_OPTIONS: readonly WorkType[] = ['feature', 'bug', 'change', 'support', 'internal'];
const PRIORITY_OPTIONS: readonly Priority[] = ['urgent', 'high', 'normal', 'low'];

/**
 * Where this work runs: the backlog, a sprint, or the hotfix track (Spec v0.5
 * §1: "Sprint (or Backlog, or Hotfix track)").
 *
 * Locked sprints are shown but cannot be chosen, and a request already in one
 * cannot be moved from here. That is v0.3's success measure — "no unagreed
 * scope changes to locked sprints" — and the server refuses it anyway; this
 * says so before the click, and says where the change is made instead.
 * Released sprints are left out: they are an account of what shipped.
 */
function SprintField({
  value,
  isHotfix,
  workspaceId,
  disabled,
  onChange,
}: {
  value: string | null;
  isHotfix: boolean;
  workspaceId: string;
  disabled: boolean;
  onChange: (patch: RequestPatch) => void;
}) {
  const sprints = useQuery(sprintsQueryOptions(workspaceId));
  const all = sprints.data ?? [];
  const current = all.find((sprint) => sprint.id === value);
  const pinned = current !== undefined && current.state !== 'planning';
  const options = pinned
    ? [current, ...all.filter((sprint) => sprint.state === 'planning')]
    : all.filter((sprint) => sprint.state !== 'released');

  return (
    <FieldShell label="Sprint">
      <select
        value={isHotfix ? 'hotfix' : (value ?? '')}
        disabled={disabled || sprints.isPending || pinned}
        title={
          pinned
            ? `${current.name} is ${current.state}. Its scope changes through a change of plan on the Sprints screen.`
            : undefined
        }
        onChange={(event) => {
          const next = event.target.value;
          if (next === 'hotfix') onChange({ isHotfix: true });
          else
            onChange({
              sprintId: next === '' ? null : next,
              ...(isHotfix && next === '' ? { isHotfix: false } : {}),
            });
        }}
        className={CONTROL_CLASS}
      >
        <option value="">Backlog</option>
        {options.map((sprint) => (
          <option
            key={sprint.id}
            value={sprint.id}
            disabled={sprint.state !== 'planning' && sprint.id !== value}
          >
            {sprint.name}
            {sprint.state === 'locked' ? ' (locked)' : ''}
            {sprint.state === 'released' ? ' (released)' : ''}
          </option>
        ))}
        <option value="hotfix">Hotfix track</option>
      </select>
      {pinned && (
        <Link to={`/sprints?sprint=${current.id}`} className="mt-1 block fs-10.5 text-accent">
          Change of plan →
        </Link>
      )}
    </FieldShell>
  );
}

/**
 * Project — the bigger deliverable this request is a part of.
 *
 * `project.manage`, which is the vendor's alone: a project is what month end is
 * grouped by. A client sees the select disabled, showing which project their
 * work sits in, rather than not seeing the field at all — "what is this part
 * of" is a fair question for them to have an answer to.
 *
 * An archived project still shows while the request is in it, so the select
 * never silently reads "None" for grouped work.
 */
function ProjectField({
  value,
  workspaceId,
  disabled,
  onChange,
}: {
  value: string | null;
  workspaceId: string;
  disabled: boolean;
  onChange: (projectId: string | null) => void;
}) {
  const projects = useQuery(deliveryProjectsQueryOptions(workspaceId));
  const options = (projects.data ?? []).filter((project) => !project.archived || project.id === value);
  return (
    <FieldShell label="Project">
      <select
        value={value ?? ''}
        disabled={disabled || projects.isPending}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        className={CONTROL_CLASS}
      >
        <option value="">None</option>
        {options.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
            {project.archived ? ' (archived)' : ''}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

/**
 * Owner — someone on the Noorix side who works on this client.
 *
 * The list is the projection's `ownerNames`, which is built from this
 * workspace's allocations, so it is exactly who the server will accept (see
 * RequestsService.assertOwnerAllocated).
 */
function OwnerField({
  value,
  workspaceId,
  disabled,
  onChange,
}: {
  value: string | null;
  workspaceId: string;
  disabled: boolean;
  onChange: (ownerId: string | null) => void;
}) {
  const projection = useQuery(projectionQueryOptions(workspaceId, 'team', 'strict-rank'));
  const names = projection.data?.ownerNames ?? {};
  return (
    <FieldShell label="Owner">
      <select
        value={value ?? ''}
        disabled={disabled || projection.isPending}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        className={CONTROL_CLASS}
      >
        <option value="">Unassigned</option>
        {Object.entries(names).map(([id, name]) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

export interface RequestFieldsProps {
  readonly workspaceId: string;
  readonly request: RequestRow;
  /**
   * Shown as the content of the request screen's Details tab: always open,
   * with no card around it and no collapse header — the tab is the header.
   */
  readonly embedded?: boolean;
}

export function RequestFields({ workspaceId, request, embedded = false }: RequestFieldsProps) {
  const capabilities = useCapabilities();
  const canReview = capabilities.has('request.review');
  // A second gate, not a synonym for the first: committing work to a week is
  // `planning.write`, which a Developer holds and a Client Owner does not.
  const canPlan = capabilities.has('planning.write');
  // Vendor-only since 21 Sep 2026 — see ProjectField's comment. `|| canReview`
  // stays because every vendor role holds both and the server accepts either.
  const canManageProjects = capabilities.has('project.manage') || canReview;
  const internal = isInternalRequest(request) ? request : null;

  // Derived here rather than read off the row: size is a label for the hours,
  // and where the bands fall is a workspace setting. Nothing is stored.
  const thresholds = useSizeThresholds();
  const size = sizeFromHours(request.approxHours, thresholds);
  const vendorName = useVendorName();

  const update = useUpdateRequest(workspaceId, request.id);
  const save = (patch: RequestPatch) => update.mutate(patch);
  // One in-flight save at a time. Two selects changed in the same instant would
  // each PATCH from the same read, and the second would report the first's value
  // as the previous one in the trail.
  const busy = update.isPending;

  // Open by default while the request is waiting on review — that is the moment
  // the fields are the point of the screen rather than a reference.
  const [openState, setOpen] = useState(internal?.needsReview === true && canReview);
  const open = embedded || openState;

  if (!canReview) {
    return (
      <section className={embedded ? '' : 'rounded-xl border border-line px-4 py-3'}>
        {!embedded && (
          <div className="mb-3 fs-11 font-bold tracking-[0.06em] text-muted uppercase">Details</div>
        )}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(7.625rem,1fr))] gap-3">
          <ReadOnlyField label="Type" value={TYPE_LABELS[request.type]} />
          <ReadOnlyField label="Priority" value={PRIORITY_LABELS[request.priority]} />
          <ReadOnlyField label="Size" value={SIZE_LABELS[size]} hint={sizeRangeLabel(size, thresholds)} />
          <ReadOnlyField label="Approx hours" value={formatHours(request.approxHours)} />
          <ReadOnlyField label="Owner" value={request.ownerName ?? 'Unassigned'} />
          <ProjectField
            value={request.projectId}
            workspaceId={workspaceId}
            disabled={!canManageProjects || busy}
            onChange={(projectId) => save({ projectId })}
          />
        </div>
        <p className="mt-3 fs-11 text-muted">
          Set by {vendorName} when they review. Size is indicative, not a quote.
        </p>
        {update.error !== null && (
          <p role="alert" className="mt-1 fs-11 text-danger">
            {update.error.message}
          </p>
        )}
      </section>
    );
  }

  const summary = [
    TYPE_LABELS[request.type],
    request.projectName ?? 'no project',
    PRIORITY_LABELS[request.priority],
    `${SIZE_LABELS[size]}${request.approxHours > 0 ? ` ~${formatHours(request.approxHours)}` : ''}`,
    request.ownerName ?? 'unassigned',
  ].join(' · ');

  return (
    <section className={embedded ? '' : 'rounded-xl border border-line px-4 py-3'}>
      {!embedded && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
        >
          <span className="fs-11 font-bold tracking-[0.06em] whitespace-nowrap text-muted uppercase">
            Fields
          </span>
          {!open && <span className="min-w-0 flex-1 truncate fs-12 text-ink-soft">{summary}</span>}
          <span className="fs-12 font-semibold whitespace-nowrap text-accent">
            {open ? 'Hide' : 'Edit fields'}
          </span>
        </button>
      )}

      {open && (
        <>
          {!embedded && (
            <p className="mt-3 mb-2.5 fs-11 text-muted">
              Every change is recorded in the trail and visible to the client.
            </p>
          )}

          <div className="grid grid-cols-[repeat(auto-fit,minmax(8.25rem,1fr))] gap-3">
            <SelectField
              label="Type"
              value={request.type}
              options={TYPE_OPTIONS}
              labelOf={(option) => TYPE_LABELS[option]}
              disabled={!canReview || busy}
              onChange={(type) => save({ type })}
            />
            <SelectField
              label="Priority"
              value={request.priority}
              options={PRIORITY_OPTIONS}
              labelOf={(option) => PRIORITY_LABELS[option]}
              disabled={!canReview || busy}
              onChange={(priority) => save({ priority })}
            />
            <ReadOnlyField label="Size" value={SIZE_LABELS[size]} hint={sizeRangeLabel(size, thresholds)} />
            <CommittedField
              label="Approx hours"
              type="number"
              value={request.approxHours === 0 ? '' : String(request.approxHours)}
              placeholder="0"
              disabled={!canReview || busy}
              onCommit={(hours) => {
                const parsed = Number(hours);
                if (!Number.isFinite(parsed) || parsed < 0) return;
                save({ approxHours: parsed });
              }}
            />
            {internal !== null && (
              <SelectField
                label="Visible to client"
                value={internal.visibleToClient ? 'yes' : 'no'}
                options={['yes', 'no'] as const}
                labelOf={(option) => (option === 'yes' ? 'Yes' : 'Internal only')}
                disabled={!canReview || busy}
                onChange={(next) => save({ visibleToClient: next === 'yes' })}
              />
            )}
            {internal !== null && canPlan && (
              <SprintField
                value={internal.sprintId}
                isHotfix={internal.isHotfix}
                workspaceId={workspaceId}
                disabled={busy}
                onChange={save}
              />
            )}
            <ProjectField
              value={request.projectId}
              workspaceId={workspaceId}
              disabled={!canManageProjects || busy}
              onChange={(projectId) => save({ projectId })}
            />
            {internal !== null && canPlan ? (
              <OwnerField
                value={internal.ownerId}
                workspaceId={workspaceId}
                disabled={busy}
                onChange={(ownerId) => save({ ownerId })}
              />
            ) : (
              <ReadOnlyField label="Owner" value={request.ownerName ?? 'Unassigned'} />
            )}
          </div>

          <div className="mt-3 flex min-h-[1.125rem] items-center gap-2 fs-11">
            {update.isPending && <span className="text-muted">Saving…</span>}
            {update.error !== null && (
              <span className={cx('text-danger')} role="alert">
                {update.error.message}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
